import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  MapPin, Plus, X, Send, Info, ExternalLink, Navigation, LocateFixed,
  // Build 96.1 — Lucide-ikoner för kartmenyn (subtila, monokrom)
  Users, TreePine, Home, Coffee, LifeBuoy, Landmark, ShoppingBasket, List,
} from "lucide-react";
import RegionMap from "@/components/RegionMap";
import { readGeoFromImage } from "@/utils/exifGeo";
import { usePageMeta } from "@/utils/usePageMeta";
import { AREA_LIST } from "@/data/areas";
import AndringsforslagButton from "@/components/AndringsforslagButton";
import EntryFootnote from "@/components/EntryFootnote";
// Build 96.2 — delad kartmeny-logik (publik karta + admin Platser-sammelvy)
import { FILTER_GROUPS, matchesFilterGroup } from "@/utils/kartmeny";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lucide-ikoner per kartmeny-nyckel — publik karta visar dem som dezent prefix.
const KARTMENY_ICONS = {
  folk_foreningar: Users,
  friluft_idrott: TreePine,
  hemma_byarna: Home,
  loppis_fika: Coffee,
  praktisk_hjalp: LifeBuoy,
  utflykter_historia: Landmark,
  vardag: ShoppingBasket,
  visa_allt: List,
};

// Category → marker-Farbe (matchar RegionMap-palett)
const CAT_COLORS = {
  "Service":         "#8E2816",
  "Företag":         "#0C4F8F",
  "Förening":        "#6b4a22",
  "Friluftsliv":     "#2A4B3C",
  "Friluftsområde":  "#1f3d2e",
  "Vandring":        "#3F6B4E",
  "Sevärdhet":       "#8E2816",
  "Natur":           "#2A4B3C",
  "Lantbruk":        "#8b6a1f",
  "Kulturminne":     "#6b2a6b",
  "Vardag":          "#8b6a1f",
  // Nya kategorier (Build 86 + 87 — generiskt platsformulär)
  "Hjärtstartare":     "#c22a1f",
  "Återvinning":       "#5d7a48",
  "Badplats":          "#2a6f9a",
  "Vindskydd":         "#5d4a2c",
  "Grillplats":        "#a04826",
  "Båtramp":           "#1f5a73",
  "Offentlig toalett": "#5d6b7a",
  "Rastplats":         "#8b6a1f",
  "Lekplats":          "#d49a2a",
  "Utsiktsplats":      "#3a5a7a",
  // Build 93 — geografische Orte
  "Ort":               "#4a3826",
  "By":                "#4a3826",
  "Socken":            "#4a3826",
  "Historisk plats":   "#6b2a6b",
};
const CAT_DOT = {
  "Service":         "bg-falu",
  "Företag":         "bg-angermanland",
  "Förening":        "bg-[#6b4a22]",
  "Friluftsliv":     "bg-pine",
  "Friluftsområde":  "bg-[#1f3d2e]",
  "Vandring":        "bg-[#3F6B4E]",
  "Sevärdhet":       "bg-falu",
  "Natur":           "bg-pine",
  "Lantbruk":        "bg-[#8b6a1f]",
  "Kulturminne":     "bg-[#6b2a6b]",
  "Vardag":          "bg-[#8b6a1f]",
  "Hjärtstartare":     "bg-[#c22a1f]",
  "Återvinning":       "bg-[#5d7a48]",
  "Badplats":          "bg-[#2a6f9a]",
  "Vindskydd":         "bg-[#5d4a2c]",
  "Grillplats":        "bg-[#a04826]",
  "Båtramp":           "bg-[#1f5a73]",
  "Offentlig toalett": "bg-[#5d6b7a]",
  "Rastplats":         "bg-[#8b6a1f]",
  "Lekplats":          "bg-[#d49a2a]",
  "Utsiktsplats":      "bg-[#3a5a7a]",
  // Build 93 — geografische Orte (Bygd/Socken/By/Historisk plats)
  "Ort":               "bg-[#4a3826]",
  "By":                "bg-[#4a3826]",
  "Socken":            "bg-[#4a3826]",
  "Historisk plats":   "bg-[#6b2a6b]",
};

// Build 96 / 96.1 / 96.2 — Filtreringen själv lever i `/utils/kartmeny.js` (delas med admin Platser-sammelvyn).
// Här mappar vi bara nyckel → ikon (publik UI). Backend/datamodell oförändrad.

const TIPP_CATEGORIES = [
  "Naturtips", "Favoritplats", "Utsiktsplats", "Bärställe", "Fiskevatten", "Svampställe", "Kulturminne",
  // Nya kategorier (P1/Datapflege): Mod kan välja direkt vid "tipsa om plats" eller skapa via admin.
  "Hjärtstartare", "Återvinning", "Badplats", "Vindskydd", "Grillplats", "Båtramp",
  "Offentlig toalett", "Rastplats", "Lekplats",
  "Annat",
];

function hasCoords(item) {
  return typeof item.lat === "number" && typeof item.lng === "number" && !(item.lat === 0 && item.lng === 0);
}

export default function KartaSida({ embedded = false, belowRubrikSlot = null }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ namn: "", beskrivning: "", kategori: "Favoritplats", avsandare: "", ort: "Backe", adress: "", bild_url: "" });
  const [tippUploading, setTippUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [foretag, setForetag] = useState([]);
  const [foreningar, setForeningar] = useState([]);
  const [karttipps, setKarttipps] = useState([]);
  const [vardagskort, setVardagskort] = useState([]);
  const [activeId, setActiveId] = useState(null);
  // Build 96.23 — expanderbara beskrivningar i panelen (Set av item-id).
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpand = (id) => setExpandedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // Build 96.23 — enkel lightbox för panelens galleri-thumbnails (klick öppnar stor bild, klick igen stänger).
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Build 96 — En enda aktiv filtergrupp-nyckel (default: "visa_allt"). Ersätter de
  // tidigare separata states activeCats/activePresetKey/mainCategoryFilter.
  const [activeGroupKey, setActiveGroupKey] = useState("visa_allt");
  const [userPos, setUserPos] = useState(null); // {lat, lng} | null
  const [geoError, setGeoError] = useState(null);
  const listRefs = useRef({});

  usePageMeta({
    title: "Karta – vidalv.live",
    description: "Interaktiv karta över Backe, Rossön och bygden. Företag, föreningar, vårdcentral, butiker och utflyktsmål – allt på en plats med vägbeskrivning.",
    path: "/karta",
    skip: embedded,
  });

  useEffect(() => {
    axios.get(`${API}/praktiskt/tjanstekort`).then(r => setForetag(r.data)).catch(() => {});
    axios.get(`${API}/foreningar`).then(r => setForeningar(r.data)).catch(() => {});
    axios.get(`${API}/karttipps`).then(r => setKarttipps(r.data)).catch(() => {});
    axios.get(`${API}/praktiskt/vardagskort`).then(r => setVardagskort(r.data)).catch(() => {});
  }, []);

  // Build 96.24 — delegated click på Leaflet-popup-bilder (data-vidalv-lightbox).
  // Popup-HTML rendas som statisk HTML av Leaflet → React kan inte binda direkt.
  // Ett globalt lyssnarpar (click + touchend) räcker och rör inget annat.
  useEffect(() => {
    const handler = (ev) => {
      const t = ev.target;
      if (!t || t.tagName !== "IMG") return;
      const url = t.getAttribute("data-vidalv-lightbox");
      if (!url) return;
      ev.preventDefault();
      ev.stopPropagation();
      setLightboxUrl(url);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  // Build 96.24 — global ESC-listener för lightbox (samma UX som GalleriStrip).
  useEffect(() => {
    if (!lightboxUrl) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setLightboxUrl(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxUrl]);

  // Samla alla placerade objekt i en gemensam lista (Firmor + Föreningar + Sevärdheter/Karttipps med koords + Friluftsområden)
  // Build 96.9 — ALARM-fix: STABILA numren från DB-fälten (karttipp_nr, foretag_nr, forening_nr, vardagskort_nr).
  // Nummern får ALDRIG härledas från array-index eller filterordning — då stämmer de inte med /mod → Platser.
  // Kort marker-label: K#7 / Fö#7 / Fr#7 / V#7. Full label i lista/popup: "Karttipp #7 – Namn".
  const placedItems = useMemo(() => {
    const rows = [];
    foretag.forEach(f => {
      if (!hasCoords(f)) return;
      rows.push({
        id: f.id,
        namn: f.foretagsnamn,
        kategori: "Företag",
        source: "foretag",
        stable_nr: f.foretag_nr ?? null,
        displayType: "Företag",
        displayShort: f.foretag_nr != null ? `Fö#${f.foretag_nr}` : "Fö",
        displayLabel: f.foretag_nr != null ? `Företag #${f.foretag_nr}` : "Företag",
        ort: f.ort,
        adress: f.adress || null,
        beskrivning: f.beskrivning || null, // Build 96.23 — expandierbar i panelen
        lat: f.lat, lng: f.lng,
        href: "/foretag#" + f.id,
        linkLabel: "Till detaljen →",
        google_maps_url: f.google_maps_url,
        media_url: f.media_url, media_typ: f.media_typ,
        kartmeny: f.kartmeny || null,
      });
    });
    foreningar.forEach(fr => {
      if (!hasCoords(fr)) return;
      rows.push({
        id: fr.id,
        namn: fr.namn,
        kategori: "Förening",
        source: "forening",
        stable_nr: fr.forening_nr ?? null,
        displayType: "Förening",
        displayShort: fr.forening_nr != null ? `Fr#${fr.forening_nr}` : "Fr",
        displayLabel: fr.forening_nr != null ? `Förening #${fr.forening_nr}` : "Förening",
        ort: fr.ort,
        adress: fr.adress || null,
        beskrivning: fr.beskrivning || null, // Build 96.23
        lat: fr.lat, lng: fr.lng,
        href: "/foreningar-evenemang#" + fr.id,
        linkLabel: "Till detaljen →",
        google_maps_url: fr.google_maps_url,
        media_url: fr.media_url, media_typ: fr.media_typ,
        kartmeny: fr.kartmeny || null,
      });
    });
    karttipps.filter(t => t.status === "Publicerat" && hasCoords(t)).forEach(t => {
      rows.push({
        id: t.id,
        namn: t.namn,
        kategori: t.kategori || "Sevärdhet",
        source: "karttipps",
        stable_nr: t.karttipp_nr ?? null,
        displayType: "Karttipp",
        displayShort: t.karttipp_nr != null ? `K#${t.karttipp_nr}` : "K",
        displayLabel: t.karttipp_nr != null ? `Karttipp #${t.karttipp_nr}` : "Karttipp",
        ort: t.kontakt || null,
        adress: t.adress || null,
        beskrivning: t.beskrivning || null, // Build 96.23 — publikt läsbar/expandierbar
        lat: t.lat, lng: t.lng,
        href: null,
        google_maps_url: t.google_maps_url,
        media_url: t.media_url, media_typ: t.media_typ,
        taggar: Array.isArray(t.taggar) ? t.taggar : [],
        galleri: Array.isArray(t.galleri) ? t.galleri : [],
        kartmeny: t.kartmeny || null,
      });
    });
    vardagskort.filter(v => hasCoords(v)).forEach(v => {
      rows.push({
        id: v.id,
        namn: v.rubrik,
        kategori: "Vardag",
        source: "vardagskort",
        stable_nr: v.vardagskort_nr ?? null,
        displayType: "Vardagskort",
        displayShort: v.vardagskort_nr != null ? `V#${v.vardagskort_nr}` : "V",
        displayLabel: v.vardagskort_nr != null ? `Vardagskort #${v.vardagskort_nr}` : "Vardagskort",
        ort: v.ort || null,
        adress: v.adress || null,
        beskrivning: v.beskrivning || null, // Build 96.23
        lat: v.lat, lng: v.lng,
        href: "/praktiskt",
        linkLabel: "Till praktiskt →",
        google_maps_url: v.google_maps_url,
        media_url: v.media_url, media_typ: v.media_typ,
        kartmeny: v.kartmeny || null,
      });
    });
    // Friluftsområden från statisk konfiguration (areas.js). Har ingen DB-stabil-nr.
    // De visas som "Område" utan #-nr — kan aldrig förväxlas med karttipps eftersom
    // typprefixet är annorlunda.
    AREA_LIST.forEach(a => {
      const gm = a.googleMaps;
      if (!gm || typeof gm.lat !== "number" || typeof gm.lng !== "number") return;
      const shortIntro = (a.intro || "").split(". ")[0].replace(/\s+/g, " ").trim();
      const descr = shortIntro.length > 140 ? shortIntro.slice(0, 137) + "…" : shortIntro;
      rows.push({
        id: `area-${a.slug}`,
        namn: a.title,
        kategori: "Friluftsområde",
        source: "area",
        stable_nr: null,
        displayType: "Område",
        displayShort: "Omr",
        displayLabel: "Område",
        ort: gm.label || null,
        adress: descr,
        lat: gm.lat, lng: gm.lng,
        href: `/friluftsliv/omrade/${a.slug}`,
        linkLabel: "Visa område →",
        google_maps_url: `https://www.google.com/maps/search/?api=1&query=${gm.lat},${gm.lng}`,
        download_url: a.downloadUrl || a.mapImage || null,
        download_name: a.downloadName || null,
        is_area: true,
      });
    });
    rows.sort((a, b) => a.namn.localeCompare(b.namn, "sv"));
    return rows;
  }, [foretag, foreningar, karttipps, vardagskort]);

  // Företag/Föreningar UTAN koordinater → listas separat
  const unplacedForetag = foretag.filter(f => !hasCoords(f))
    .sort((a, b) => (a.foretagsnamn || "").localeCompare(b.foretagsnamn || "", "sv"));
  const unplacedForeningar = foreningar.filter(fr => !hasCoords(fr))
    .sort((a, b) => (a.namn || "").localeCompare(b.namn || "", "sv"));

  // Unika kategorier (behålls för intern debug/eventuell senare användning;
  // visas dock inte längre publikt sedan Build 96).
  const availableCats = useMemo(() => {
    const set = new Set();
    for (const p of placedItems) {
      if (p.kategori) set.add(p.kategori);
      if (Array.isArray(p.taggar)) {
        for (const t of p.taggar) {
          if (t && typeof t === "string") set.add(t.trim());
        }
      }
    }
    return Array.from(set).filter(Boolean);
  }, [placedItems]);

  // Build 96 — En enda matchningskälla: vald filtergrupp.
  const activeGroup = useMemo(
    () => FILTER_GROUPS.find((g) => g.key === activeGroupKey) || FILTER_GROUPS[FILTER_GROUPS.length - 1],
    [activeGroupKey]
  );
  const isFiltering = activeGroupKey !== "visa_allt";
  const isVisibleItem = (p) => matchesFilterGroup(p, activeGroup);
  const visibleItems = useMemo(
    () => placedItems.filter(isVisibleItem),
    [placedItems, activeGroup]
  );

  const mapMarkers = useMemo(() => {
    const m = visibleItems.map(p => {
      let extraHtml = "";
      if (p.is_area) {
        const links = [];
        links.push(`<a class="vidalv-popup-cta" href="${escapeAttr(p.href)}">Visa område →</a>`);
        if (p.download_url) {
          links.push(`<a class="vidalv-popup-link" href="${escapeAttr(p.download_url)}" target="_blank" rel="noopener noreferrer">Ladda ner karta</a>`);
        }
        if (p.google_maps_url) {
          links.push(`<a class="vidalv-popup-link" href="${escapeAttr(p.google_maps_url)}" target="_blank" rel="noopener noreferrer">Visa på karta</a>`);
        }
        extraHtml = `<div class="vidalv-popup-actions">${links.join("")}</div>`;
      }
      // Build 96.23 — tags dolda publikt (data ligger kvar, endast presentation ändras)
      const tagsHtml = "";
      // Build 93 — galleri-thumbnails i popupen (max 3, dezent).
      // Build 96.24 — klickbara: data-vidalv-lightbox-attribut fångas av delegated listener.
      const photoSrc = (rel) => `${process.env.REACT_APP_BACKEND_URL}${rel}`;
      const photosHtml = Array.isArray(p.galleri) && p.galleri.length
        ? `<div class="vidalv-popup-photos">${p.galleri.slice(0, 3).map(u => `<img src="${escapeAttr(photoSrc(u))}" data-vidalv-lightbox="${escapeAttr(photoSrc(u))}" alt="" loading="lazy" style="cursor: zoom-in;" />`).join("")}</div>`
        : "";
      // Build 89 — valfri medie-länk i popupen
      const MEDIA_LABELS = { Video: "Se filmen", Artikel: "Läs mer", Audio: "Lyssna", Dokument: "Öppna dokument" };
      const mediaSafe = p.media_url && /^https?:\/\//i.test((p.media_url || "").trim()) && MEDIA_LABELS[p.media_typ];
      const mediaHtml = mediaSafe
        ? `<p class="vidalv-popup-media"><a href="${escapeAttr(p.media_url.trim())}" target="_blank" rel="noopener noreferrer">${MEDIA_LABELS[p.media_typ]} →</a></p>`
        : "";
      return {
        id: p.id,
        lng: p.lng,
        lat: p.lat,
        cat: p.kategori,
        // Build 96.9 — kort marker-label ("K#7"), stabilt från DB (karttipp_nr/foretag_nr/…).
        number: p.displayShort,
        hoverLabel: escapeHtml(p.namn),
        // Popup: full label ("Karttipp #7 – Namn") — omöjligt att förväxla över källor.
        popupHtml: `<h4>${escapeHtml(p.displayLabel)} – ${escapeHtml(p.namn)}</h4>${p.adress ? `<p>${escapeHtml(p.adress)}</p>` : ""}<p class="cat-badge">${escapeHtml(p.kategori)}</p>${photosHtml}${tagsHtml}${mediaHtml}${extraHtml}`,
        onClick: () => {
          setActiveId(p.id);
          const el = listRefs.current[p.id];
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        },
      };
    });
    if (userPos) {
      m.push({
        id: "__user__",
        lng: userPos.lng, lat: userPos.lat,
        cat: "User",
        popupHtml: `<h4>Din position</h4><p>Ungefärlig plats (GPS).</p>`,
        isUser: true,
      });
    }
    return m;
  }, [visibleItems, userPos]);

  const handleSubmitTipp = async () => {
    if (!form.namn.trim() || !form.avsandare.trim()) return;
    try {
      await axios.post(`${API}/karttipps/submit`, {
        namn: form.namn, beskrivning: form.beskrivning, kategori: form.kategori,
        lat: userPos?.lat || 0, lng: userPos?.lng || 0,
        adress: form.adress || null,
        avsandare: form.avsandare, kontakt: form.ort,
        bild_url: form.bild_url || null,
      });
      setSubmitted(true);
      setShowForm(false);
      setForm({ namn: "", beskrivning: "", kategori: "Favoritplats", avsandare: "", ort: "Backe", adress: "", bild_url: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch {}
  };

  const handleTippImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return;
    setTippUploading(true);
    try {
      // Läs EXIF-GPS innan upload (om bilden har det) → auto-fyll position
      const geo = await readGeoFromImage(file);
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd);
      setForm(p => ({
        ...p,
        bild_url: res.data.url,
        ...(geo ? { adress: p.adress || `Plus-Code: ${geo.plusCode}` } : {}),
      }));
      if (geo) {
        // Sätt user-pos → markören placeras på exakt foto-position
        setUserPos({ lat: geo.lat, lng: geo.lng });
      }
    } catch {}
    setTippUploading(false);
  };

  const locateMe = () => {
    setGeoError(null);
    if (!("geolocation" in navigator)) {
      setGeoError("Din webbläsare stöder inte geolocation.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => {
        setGeoError(err.code === 1
          ? "Du nekade platsåtkomst. Tillåt det i webbläsarens inställningar för att använda funktionen."
          : "Det gick inte att hämta din position.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };
  const clearMe = () => { setUserPos(null); setGeoError(null); };

  // Beräkna avstånd (km) med Haversine
  const distKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const toRad = (v) => v * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLng/2)**2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  const nearestFive = useMemo(() => {
    if (!userPos) return [];
    return placedItems
      .map(p => ({ ...p, dist: distKm(userPos.lat, userPos.lng, p.lat, p.lng) }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 5);
  }, [userPos, placedItems]);

  // Compute center + zoom from placed items
  let initialCenter = [16.40, 63.82];
  let initialZoom = 10.6;
  if (userPos) {
    initialCenter = [userPos.lng, userPos.lat];
    initialZoom = 11.5;
  } else if (placedItems.length === 1) {
    initialCenter = [placedItems[0].lng, placedItems[0].lat];
    initialZoom = 12;
  } else if (placedItems.length > 1) {
    const lats = placedItems.map(p => p.lat);
    const lngs = placedItems.map(p => p.lng);
    initialCenter = [
      (Math.min(...lngs) + Math.max(...lngs)) / 2,
      (Math.min(...lats) + Math.max(...lats)) / 2,
    ];
    const spread = Math.max(
      Math.max(...lats) - Math.min(...lats),
      (Math.max(...lngs) - Math.min(...lngs)) * 0.5,
    );
    if (spread > 0.3) initialZoom = 9;
    else if (spread > 0.15) initialZoom = 9.8;
    else if (spread > 0.05) initialZoom = 10.6;
    else initialZoom = 11.5;
  }

  return (
    <div data-testid="karta-page" className={embedded ? "" : "max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12"}>
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Karta</p>
            <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-2">
              <span className="jugendstil-accent">Bygden i översikt</span>
            </h1>
            <p className="text-sm text-bark max-w-xl leading-relaxed mt-3">
              Numrerade cirklar visar företag, föreningar och sevärdheter som finns bekräftade med koordinater. Klicka på en siffra för att se detaljen i listan.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button data-testid="locate-me-btn" onClick={userPos ? clearMe : locateMe}
              className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-sm transition-colors ${userPos ? "bg-pine text-parchment" : "bg-parchment border border-warm-border text-ink hover:border-bark/40"}`}>
              {userPos ? <><LocateFixed size={14} /> Visar din plats</> : <><Navigation size={14} /> Nära mig</>}
            </button>
            <button data-testid="add-tipp-btn" onClick={() => setShowForm(!showForm)}
              className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-sm transition-colors ${showForm ? "bg-falu text-parchment" : "bg-parchment border border-warm-border text-ink hover:border-bark/40"}`}>
              {showForm ? <><X size={14} /> Stäng</> : <><Plus size={14} /> Tipsa om plats</>}
            </button>
          </div>
        </div>
      )}

      {embedded && (
        // Kompakt header för start-sidans inbäddning: en rad med "Nära mig" + "Tipsa"
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Karta</p>
            <h2 className="font-fraktur-smooth text-lg sm:text-xl text-falu tracking-wide leading-tight">Bygden i översikt</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="locate-me-btn" onClick={userPos ? clearMe : locateMe}
              className={`flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3 py-2 rounded-sm transition-colors ${userPos ? "bg-pine text-parchment" : "bg-parchment border border-warm-border text-ink hover:border-bark/40"}`}>
              {userPos ? <><LocateFixed size={13} /> Visar din plats</> : <><Navigation size={13} /> Nära mig</>}
            </button>
            <button data-testid="add-tipp-btn" onClick={() => setShowForm(!showForm)}
              className={`flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3 py-2 rounded-sm transition-colors ${showForm ? "bg-falu text-parchment" : "bg-parchment border border-warm-border text-ink hover:border-bark/40"}`}>
              {showForm ? <><X size={13} /> Stäng</> : <><Plus size={13} /> Tipsa om plats</>}
            </button>
          </div>
        </div>
      )}

      {/* Build 96.13 — CTA-slot direkt under kart-rubriken, före själva kartan. */}
      {embedded && belowRubrikSlot}

      {geoError && (
        <div data-testid="geo-error" className="mb-4 bg-falu/10 border border-falu/30 text-falu text-sm rounded-sm px-3 py-2">
          {geoError}
        </div>
      )}

      {/* Build 96 / 96.1 — Ett enda publikt filtermeny (åtta intent-grupper, alfabetiskt).
          Ersätter både den gamla "Kategori"-chipraden och "Vad vill du göra idag?"-raden.
          Backend/datamodell oförändrad — matchning via kartmeny → source/kategori/tag. */}
      <div data-testid="map-filter-row" className="mb-5 -mx-4 px-4 sm:mx-0 sm:px-0">
        <p className="font-body text-[11px] font-semibold text-bark/55 uppercase tracking-[0.22em] mb-2.5">
          Vad söker du i bygden?
        </p>
        <div className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible scrollbar-thin">
          {FILTER_GROUPS.map((g) => {
            const active = activeGroupKey === g.key;
            const Icon = KARTMENY_ICONS[g.key];
            return (
              <button
                key={g.key}
                type="button"
                data-testid={`map-filter-${g.key}`}
                onClick={() => setActiveGroupKey(g.key)}
                aria-pressed={active}
                className={`shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors border ${
                  active
                    ? "bg-ink text-parchment border-ink shadow-sm"
                    : "bg-parchment text-bark border-warm-border hover:border-falu/60 hover:text-falu"
                }`}
              >
                {Icon && <Icon size={14} strokeWidth={1.5} aria-hidden="true" />}
                <span>{g.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* MapLibre map with numbered markers */}
      <div data-testid="main-map-container" className="relative w-full h-[60vh] min-h-[420px] max-h-[620px] rounded-sm overflow-hidden border border-warm-border bg-parchment mb-5">
        <RegionMap
          variant="page"
          center={initialCenter}
          zoom={initialZoom}
          markers={mapMarkers}
          className="absolute inset-0"
        />
        <div className="absolute top-3 left-3 bg-parchment/95 border border-warm-border rounded-sm px-3 py-2 text-xs text-bark font-body shadow-sm">
          <span data-testid="placed-count" className="font-semibold text-ink">{visibleItems.length}</span>
          {isFiltering && <span className="text-bark/50"> / {placedItems.length}</span>}
          {" "}plats{visibleItems.length === 1 ? "" : "er"} med bekräftade koordinater
        </div>
      </div>

      {/* Nearest 5 – when user location is known */}
      {userPos && nearestFive.length > 0 && (
        <section data-testid="nearest-five" className="mb-8 bg-pine/5 border border-pine/30 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <LocateFixed size={16} className="text-pine" />
            <h2 className="font-heading text-lg sm:text-xl font-semibold text-ink">Närmast dig just nu</h2>
          </div>
          <ol className="flex flex-col gap-2">
            {nearestFive.map((p) => {
              const color = CAT_COLORS[p.kategori] || CAT_COLORS["Sevärdhet"];
              return (
                <li key={p.id} data-testid={`nearest-${p.source}-${p.stable_nr ?? p.id}`} className="flex items-center gap-3 bg-parchment border border-warm-border rounded-sm px-3 py-2">
                  <span
                    className="shrink-0 flex items-center justify-center min-w-[2.5rem] h-6 rounded-full text-[10px] font-bold px-1.5"
                    style={{ backgroundColor: color, color: "#FDFBF7" }}
                    title={p.displayLabel}
                  >
                    {p.displayShort}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink leading-tight truncate">{p.namn}</p>
                    {p.adress && <p className="text-xs text-bark/60 truncate">{p.adress}</p>}
                  </div>
                  <span className="text-xs font-semibold text-pine shrink-0">{p.dist < 1 ? `${Math.round(p.dist*1000)} m` : `${p.dist.toFixed(1)} km`}</span>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {/* Honest note */}
      <div className="flex items-start gap-2 bg-bone-alt/50 border border-warm-border rounded-sm p-3 text-xs text-bark/70 leading-relaxed mb-8">
        <Info size={14} className="shrink-0 mt-0.5 text-bark" />
        <p>
          Endast verksamheter som går att hitta med exakt adress (Google Maps / OpenStreetMap) placeras på kartan.
          Övriga finns i listan längre ned. Har du rätt adress eller plusskod för ditt företag? Skicka den via
          &laquo;Föreslå ändring&raquo; &ndash; vi lägger till pinnen.
        </p>
      </div>

      {showForm && (
        <div data-testid="tipp-form" className="bg-parchment border border-warm-border rounded-sm p-5 mb-8">
          <h3 className="font-heading text-xl font-semibold text-ink mb-2">Tipsa om en plats</h3>
          <p className="text-sm text-bark mb-4">Beskriv platsen du vill lägga till. Vi verifierar och lägger till den på kartan.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-bark mb-1">Platsnamn *</label>
              <input data-testid="tipp-namn" type="text" value={form.namn} onChange={e => setForm(p => ({ ...p, namn: e.target.value }))} placeholder="T.ex. Bästa utsikten vid älven" className="w-full px-3 py-2 border border-warm-border rounded-sm text-sm bg-bone font-body focus:border-falu focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-bark mb-1">Ort</label>
              <select value={form.ort} onChange={e => setForm(p => ({ ...p, ort: e.target.value }))} className="w-full px-3 py-2 border border-warm-border rounded-sm text-sm bg-bone font-body focus:border-falu focus:outline-none">
                <option value="Backe">Backe</option>
                <option value="Rossön">Rossön</option>
                <option value="Annat">Annat</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-bark mb-1">Kategori</label>
              <select value={form.kategori} onChange={e => setForm(p => ({ ...p, kategori: e.target.value }))} className="w-full px-3 py-2 border border-warm-border rounded-sm text-sm bg-bone font-body focus:border-falu focus:outline-none">
                {TIPP_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-bark mb-1">Ditt namn *</label>
              <input data-testid="tipp-avsandare" type="text" value={form.avsandare} onChange={e => setForm(p => ({ ...p, avsandare: e.target.value }))} className="w-full px-3 py-2 border border-warm-border rounded-sm text-sm bg-bone font-body focus:border-falu focus:outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-bark mb-1">Beskrivning</label>
              <input data-testid="tipp-beskrivning" type="text" value={form.beskrivning} onChange={e => setForm(p => ({ ...p, beskrivning: e.target.value }))} placeholder="Beskriv platsen kort" className="w-full px-3 py-2 border border-warm-border rounded-sm text-sm bg-bone font-body focus:border-falu focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-bark mb-1">Adress / lokalisering</label>
              <input data-testid="tipp-adress" type="text" value={form.adress} onChange={e => setForm(p => ({ ...p, adress: e.target.value }))} placeholder="T.ex. Strandvägen vid bron" className="w-full px-3 py-2 border border-warm-border rounded-sm text-sm bg-bone font-body focus:border-falu focus:outline-none" />
            </div>
            <div className="sm:col-span-3">
              <label className="block text-xs font-semibold text-bark mb-1">Bifoga bild (frivilligt)</label>
              {form.bild_url ? (
                <div className="flex items-center gap-3">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${form.bild_url}`} alt="" className="w-20 h-20 object-cover rounded-sm border border-warm-border" />
                  <button type="button" onClick={() => setForm(p => ({ ...p, bild_url: "" }))} className="text-xs text-falu hover:text-falu-hover underline" data-testid="tipp-bild-remove">Ta bort bild</button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 text-sm text-pine hover:text-pine-hover cursor-pointer">
                  {tippUploading ? "Laddar upp..." : "Välj bild"}
                  <input type="file" accept="image/*" onChange={handleTippImageUpload} disabled={tippUploading} className="hidden" data-testid="tipp-bild-input" />
                </label>
              )}
              <p className="text-[11px] text-bark/60 mt-1">
                Tips: om bilden är tagen på platsen läser vi automatiskt in koordinaterna från bildens GPS-data.
              </p>
            </div>
            <div className="flex items-end sm:col-span-3">
              <button data-testid="submit-tipp" onClick={handleSubmitTipp} disabled={!form.namn.trim() || !form.avsandare.trim()}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-falu hover:bg-falu-hover disabled:opacity-40 text-parchment text-sm font-semibold px-4 py-2.5 rounded-sm transition-colors">
                <Send size={14} /> Skicka tips
              </button>
            </div>
          </div>
        </div>
      )}

      {submitted && (
        <div className="bg-pine text-parchment text-sm font-semibold px-4 py-3 rounded-sm mb-8 text-center">
          Tack! Ditt karttips har skickats in. Vi verifierar och lägger till det.
        </div>
      )}

      {/* Numrerad lista till kartan – göms när vi är inbäddade på startsidan (länken
          "Öppna karta i fullvy" leder dit listan finns i sin helhet) */}
      {placedItems.length > 0 && !embedded && (
        <section data-testid="placed-list" className="mb-12">
          <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-4">
            På kartan <span className="text-bark/40 font-normal text-lg">
              ({visibleItems.length}{isFiltering && ` av ${placedItems.length}`})
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {visibleItems.map((p) => {
              const color = CAT_COLORS[p.kategori] || CAT_COLORS["Sevärdhet"];
              const isActive = activeId === p.id;
              return (
                <div
                  key={p.id}
                  ref={el => { if (el) listRefs.current[p.id] = el; }}
                  data-testid={`placed-item-${p.source}-${p.stable_nr ?? p.id}`}
                  className={`flex items-start gap-3 bg-parchment border rounded-sm p-3 transition-colors ${isActive ? "border-falu shadow-sm" : "border-warm-border"}`}
                >
                  <span
                    className="shrink-0 flex items-center justify-center min-w-[2.75rem] h-7 rounded-full text-[11px] font-bold px-1.5"
                    style={{ backgroundColor: color, color: "#FDFBF7" }}
                    title={p.displayLabel}
                  >
                    {p.displayShort}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-ink leading-tight">
                        <span className="text-[10px] font-mono font-semibold text-bark/55 mr-1.5">{p.displayLabel}</span>
                        {p.namn}
                      </p>
                      <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${CAT_DOT[p.kategori] || "bg-falu"}`} />
                    </div>
                    {(p.adress || p.ort) && (
                      <p className="text-xs text-bark/70 mt-0.5 leading-snug">
                        {p.adress || p.ort}
                      </p>
                    )}
                    {/* Build 96.23 — publikt läsbar beskrivning med Läs mer/mindre-toggle. */}
                    {p.beskrivning && p.beskrivning.trim().length > 0 && (() => {
                      const full = p.beskrivning.trim();
                      const isLong = full.length > 140;
                      const expanded = expandedIds.has(p.id);
                      const shown = !isLong || expanded ? full : full.slice(0, 140).replace(/\s+\S*$/, "") + "…";
                      return (
                        <div className="mt-1.5" data-testid={`plats-beskrivning-${p.id}`}>
                          <p className="text-xs sm:text-sm text-bark/85 leading-relaxed whitespace-pre-line">{shown}</p>
                          {isLong && (
                            <button
                              type="button"
                              onClick={(ev) => { ev.stopPropagation(); toggleExpand(p.id); }}
                              data-testid={`plats-beskrivning-toggle-${p.id}`}
                              className="mt-0.5 text-[11px] text-falu hover:text-falu-hover underline"
                            >
                              {expanded ? "Visa mindre" : "Läs mer"}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    {/* Build 96.23 — tags dolda publikt. Data bevaras — visas endast i admin (PlatserOversiktPanel). */}
                    {/* Build 93 — galleri-thumbnails (max 3, dezent). Build 96.23 — klickbara, öppnar större bild. */}
                    {Array.isArray(p.galleri) && p.galleri.length > 0 && (
                      <div className="flex gap-1 mt-1.5" data-testid={`plats-galleri-${p.id}`}>
                        {p.galleri.slice(0, 3).map((u, i) => {
                          const full = `${process.env.REACT_APP_BACKEND_URL}${u}`;
                          return (
                            <button
                              type="button"
                              key={i}
                              onClick={(ev) => { ev.stopPropagation(); setLightboxUrl(full); }}
                              data-testid={`plats-galleri-thumb-${p.id}-${i}`}
                              aria-label={`Visa bild ${i + 1}`}
                              className="p-0 border-0 bg-transparent cursor-zoom-in"
                            >
                              <img
                                src={full}
                                alt=""
                                loading="lazy"
                                className="w-12 h-12 object-cover rounded-sm border border-warm-border/60 hover:border-falu/60 transition-colors"
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 mt-1.5">
                      {p.href && (
                        <a href={p.href} data-testid={`placed-link-${p.source}-${p.stable_nr ?? p.id}`} className="text-xs font-semibold text-falu hover:text-falu-hover no-underline">
                          {p.linkLabel || "Till detaljen →"}
                        </a>
                      )}
                      {p.download_url && (
                        <a
                          href={p.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={p.download_name || undefined}
                          data-testid={`placed-download-${p.source}-${p.stable_nr ?? p.id}`}
                          className="text-xs font-semibold text-pine hover:text-pine-hover no-underline inline-flex items-center gap-1"
                        >
                          Ladda ner karta
                        </a>
                      )}
                      {p.google_maps_url && (
                        <a href={p.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-bark/70 hover:text-falu no-underline inline-flex items-center gap-1">
                          {p.is_area ? "Visa på karta" : "Google Maps"} <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    {/* Build 90 — Föreslå ändring på karttipps-poster (de saknar egen detaljsida) */}
                    {p.source === "karttipps" && (
                      <>
                        <EntryFootnote
                          kalla={p.kalla || p.kalltext}
                          senast={p.senast_andrad || p.senast_kontrollerad}
                          uppdaterad_av={p.andrad_av}
                          testIdPrefix={`plats-${p.id}`}
                          className="mt-2"
                        />
                        <div className="mt-2 pt-2 border-t border-warm-border/40">
                          <AndringsforslagButton typ="karttipps" referensId={p.id} referensNamn={p.namn} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Utan koordinater */}
      {(unplacedForetag.length > 0 || unplacedForeningar.length > 0) && !embedded && (
        <section data-testid="unplaced-list" className="mb-12">
          <h2 className="font-heading text-2xl font-semibold text-ink tracking-tight mb-2">
            Ännu inte på kartan
          </h2>
          <p className="text-xs text-bark/60 mb-4 leading-relaxed max-w-2xl">
            Dessa verksamheter saknar ännu en bekräftad adress i Google Maps / OpenStreetMap. Har du hemadress eller plusskod? Skicka den via
            &laquo;Föreslå ändring&raquo; på respektive sida, så placerar redaktionen dem på kartan.
          </p>

          {unplacedForetag.length > 0 && (
            <div className="mb-6">
              <p className="font-body text-xs font-semibold text-bark/60 uppercase tracking-[0.15em] mb-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[#4a6075] mr-1.5 align-middle" />
                Företag ({unplacedForetag.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {unplacedForetag.map(f => (
                  <div key={f.id} data-testid={`unplaced-foretag-${f.id}`} className="flex items-start gap-2 bg-bone-alt/40 border border-warm-border rounded-sm p-2.5">
                    <MapPin size={13} className="text-bark/40 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-ink leading-tight">{f.foretagsnamn}</p>
                      {f.ort && <p className="text-xs text-bark/60 mt-0.5">{f.ort}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {unplacedForeningar.length > 0 && (
            <div>
              <p className="font-body text-xs font-semibold text-bark/60 uppercase tracking-[0.15em] mb-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[#6b4a22] mr-1.5 align-middle" />
                Föreningar ({unplacedForeningar.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {unplacedForeningar.map(fr => (
                  <div key={fr.id} data-testid={`unplaced-forening-${fr.id}`} className="flex items-start gap-2 bg-bone-alt/40 border border-warm-border rounded-sm p-2.5">
                    <MapPin size={13} className="text-bark/40 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-ink leading-tight">{fr.namn}</p>
                      {fr.ort && <p className="text-xs text-bark/60 mt-0.5">{fr.ort}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Build 96.23 — global lightbox för galleri-thumbnails i panelen. ESC + klick utanför stänger. */}
      {lightboxUrl && (
        <div
          data-testid="karta-lightbox"
          className="fixed inset-0 z-[1000] bg-ink/95 flex items-center justify-center p-4 sm:p-10"
          onClick={() => setLightboxUrl(null)}
          onKeyDown={(e) => { if (e.key === "Escape") setLightboxUrl(null); }}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          <button
            type="button"
            data-testid="karta-lightbox-close"
            onClick={() => setLightboxUrl(null)}
            aria-label="Stäng"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-parchment/85 hover:text-parchment text-3xl leading-none font-light w-11 h-11 flex items-center justify-center rounded-full hover:bg-parchment/10 transition-colors"
          >
            ×
          </button>
          <img
            src={lightboxUrl}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-sm shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

function escapeHtml(str) {
  if (!str) return "";
  return String(str).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

function escapeAttr(str) {
  // Use the same escaping; safe enough for href values vi kontrollerar själva.
  return escapeHtml(str);
}
